import * as React from 'react'
/**
 * A custom hook that debounces a function call.
 * @param callback The function to debounce
 * @param delay Delay in milliseconds
 * @returns An object with the debounced function and a cancel method
 * @example
 * const { debouncedFunction, cancel } = useDebouncedCallback(() => {
 *   console.log('Debounced function called');
 * }, 500);
 *
 * // Use the debounced function
 * debouncedFunction();
 *
 * // Cancel the pending execution
 * cancel();
 */
export function useDebouncedCallback<T extends unknown[]>(callback: (...args: T) => void, delay = 300) {
  const callbackRef = React.useRef(callback)
  const timeoutRef = React.useRef<NodeJS.Timeout>(null)

  const cancel = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const debouncedCallback = React.useCallback(
    (...args: T) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args)
      }, delay)
    },
    [delay]
  )

  React.useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  return { debouncedCallback, cancel }
}
